import React, { useState } from 'react';
import useSWR from 'swr';
import { orgApi } from '../utils/apiV2';
import { CreditCard, Copy, Check, ExternalLink, LifeBuoy } from 'lucide-react';
import { toast } from 'sonner';

/**
 * PaymentSupportCard — a compact "need help with payments?" helper for the
 * financial overview. Surfaces the operator's connected Stripe account ID
 * (one-tap copy) right next to Stripe's support + dashboard links, so if a
 * charge/payout issue ever comes up they can jump straight to Stripe with
 * their account ID in hand and get it resolved fast.
 *
 * Reads the org from the shared SWR key `operator-organization` (same key
 * `useOperatorLocale` uses) so it dedupes — no extra network request.
 */
export const PaymentSupportCard = () => {
  const { data: org } = useSWR(
    'operator-organization',
    () => orgApi.getOrganization().then((res) => res.data),
    { revalidateOnFocus: false, dedupingInterval: 60000 }
  );

  const [copied, setCopied] = useState(false);
  const acctId = org?.stripeConnectAccountId || org?.stripe_connect_account_id || '';
  const chargesEnabled =
    org?.stripeConnectChargesEnabled ?? org?.stripe_connect_charges_enabled ?? false;

  const copyAccountId = async () => {
    if (!acctId) return;
    try {
      await navigator.clipboard.writeText(acctId);
      setCopied(true);
      toast.success('Stripe account ID copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — long-press to copy manually');
    }
  };

  return (
    <div
      data-testid="payment-support-card"
      className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-4 sm:p-5"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* Left: heading + account id */}
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600">
              <CreditCard className="w-4 h-4" />
            </span>
            <h3 className="text-sm font-medium text-gray-900">Need help with payments?</h3>
          </div>
          <p className="text-xs text-gray-500 mb-2">
            Charges &amp; payouts run on your Stripe account. Keep your account ID handy for support.
          </p>

          {acctId ? (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">Stripe account ID</span>
              <code
                data-testid="stripe-account-id"
                className="px-2 py-1 rounded-md bg-gray-50 border border-gray-200 text-xs font-mono text-gray-700 select-all break-all"
              >
                {acctId}
              </code>
              <button
                type="button"
                onClick={copyAccountId}
                data-testid="copy-stripe-account-id"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-indigo-600 hover:bg-indigo-50 transition-colors"
                aria-label="Copy Stripe account ID"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
              {!chargesEnabled && (
                <span className="text-[11px] text-amber-600">• charges not fully enabled</span>
              )}
            </div>
          ) : (
            <p className="text-xs text-amber-600">
              No Stripe account connected yet — finish Stripe setup in Settings → Payments to accept payments.
            </p>
          )}
        </div>

        {/* Right: support + dashboard links */}
        <div className="flex items-center gap-2 shrink-0">
          <a
            href="https://support.stripe.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="stripe-support-link"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition-colors shadow-[0_8px_24px_rgba(79,70,229,0.18)]"
          >
            <LifeBuoy className="w-3.5 h-3.5" />
            Contact Stripe Support
          </a>
          <a
            href="https://dashboard.stripe.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="stripe-dashboard-link"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Stripe Dashboard
          </a>
        </div>
      </div>
    </div>
  );
};

export default PaymentSupportCard;
